import api, { directApi } from './api';
import offlineApi from './offlineApi';
import { invalidateSearchCache } from '../utils/searchCache';

// Choose the appropriate API instance based on environment
const apiInstance = process.env.REACT_APP_API_URL ? directApi : api;

/**
 * Box Service
 * Handles operations related to boxes/containers
 * Enhanced with offline support for PWA functionality
 */

// Online-only version - removed isOffline function

/**
 * Get all boxes (formerly albums)
 * @returns {Promise<Array>} Array of box objects
 */
const getAllBoxes = async () => {
  try {
    console.log('Getting all boxes...');
    
    // Add timestamp for cache busting
    const timestamp = new Date().getTime();
    const response = await apiInstance.get(`/boxes?_t=${timestamp}`);
    
    if (!response.data) {
      console.warn('API returned no box data');
      return [];
    }
    
    return response.data;
  } catch (error) {
    console.error('Error getting boxes:', error);
    throw error;
  }
};

/**
 * Get a box by ID
 * @param {string|number} id - Box ID
 * @returns {Promise<Object>} Box object
 */
const getBoxById = async (id) => {
  try {
    console.log(`Getting box with ID: ${id}`);
    
    // Add timestamp for cache busting
    const timestamp = new Date().getTime();
    const response = await apiInstance.get(`/boxes/${id}?_t=${timestamp}`);
    
    if (!response.data) {
      console.warn(`No box found with ID: ${id}`);
      return null;
    }
    
    return response.data;
  } catch (error) {
    console.error(`Error getting box ${id}:`, error);
    throw error;
  }
};

/**
 * Create a new box
 * @param {Object} boxData - Box data
 * @returns {Promise<Object>} Created box
 */
const createBox = async (boxData) => {
  // Set default name if not provided
  const data = {
    name: 'New Box',
    description: '',
    ...boxData,
  };
  
  try {
    // Create box through API
    const response = await apiInstance.post('/boxes', data);
    
    if (!response.data) {
      throw new Error('API returned empty response when creating box');
    }
    
    // Invalidate search cache since a new box was created
    invalidateSearchCache();
    
    console.log('Box created successfully:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error creating box:', error);
    
    throw error;
  }
};

/**
 * Update a box
 * @param {string|number} id - Box ID
 * @param {Object} boxData - Updated box data
 * @returns {Promise<Object>} Updated box
 */
const updateBox = async (id, boxData) => {
  try {
    console.log(`Updating box ${id} via API`);
    
    // Add timestamp for cache busting
    const timestamp = new Date().getTime();
    const response = await apiInstance.put(`/boxes/${id}?_t=${timestamp}`, boxData);
    
    if (!response.data) {
      throw new Error(`Failed to update box ${id}: API returned empty response`);
    }
    
    // Invalidate search cache since box was updated
    invalidateSearchCache();
    
    console.log(`Box ${id} updated successfully`);
    return response.data;
  } catch (error) {
    console.error(`Error updating box ${id}:`, error);
    throw error;
  }
};

/**
 * Delete a box
 * @param {string|number} id - Box ID
 * @returns {Promise<Object>} Delete operation result
 */
const deleteBox = async (id) => {
  try {
    console.log(`Deleting box ${id} via API`);
    
    // Add timestamp for cache busting
    const timestamp = new Date().getTime();
    const response = await apiInstance.delete(`/boxes/${id}?_t=${timestamp}`);
    
    // Invalidate search cache since box was deleted
    invalidateSearchCache();
    
    return response.data || { success: true, id };
  } catch (error) {
    console.error(`Error deleting box ${id}:`, error);
    throw error;
  }
};

/**
 * Get all items in a box
 * @param {string|number} boxId - Box ID
 * @returns {Promise<Array>} Array of item objects
 */
const getBoxItems = async (boxId) => {
  try {
    console.log(`Getting items for box ${boxId} via API`);
    
    // Add timestamp for cache busting
    const timestamp = new Date().getTime();
    // Use the correct endpoint that filters items by box_id
    const response = await apiInstance.get(`/items?box_id=${boxId}&_t=${timestamp}`);
    
    if (!response.data) {
      console.warn(`No items found for box ${boxId}`);
      return [];
    }
    
    return response.data;
  } catch (error) {
    console.error(`Error getting items for box ${boxId}:`, error);
    throw error;
  }
};

/**
 * Search boxes by term
 * @param {string} searchTerm - Search term to find in name, description, etc.
 * @returns {Promise<Array>} Array of matching boxes
 */
const searchBoxes = async (searchTerm) => {
  try {
    if (!searchTerm) {
      // If no search term, return all boxes
      return await getAllBoxes();
    }
    
    console.log(`Searching boxes via API for: ${searchTerm}`);
    
    // Add timestamp for cache busting
    const timestamp = new Date().getTime();
    const response = await apiInstance.get(`/search/boxes?q=${encodeURIComponent(searchTerm)}&_t=${timestamp}`);
    
    return response.data || [];
  } catch (error) {
    console.error(`Error searching boxes for "${searchTerm}":`, error);
    throw error;
  }
};

/**
 * Check for offline data availability
 * This method ensures that the offline data store has necessary data
 * @returns {Promise<boolean>} True if offline data is available
 */
const checkOfflineData = async () => {
  try {
    console.log('Checking offline data availability...');
    
    // First check if offline mode is enabled
    const offlineModeEnabled = await offlineApi.isOfflineModeEnabled();
    console.log(`Offline mode enabled: ${offlineModeEnabled}`);
    
    // If we're offline or in offline mode, try to prefetch essential data
    if (!navigator.onLine || offlineModeEnabled) {
      console.log('Device is offline or offline mode is enabled - prefetching data...');
      await offlineApi.prefetchEssentialData();
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error checking offline data:', error);
    return false;
  }
};

// Export the public API
const boxService = {
  getAllBoxes,
  getBoxById,
  createBox,
  updateBox,
  deleteBox,
  getBoxItems,
  searchBoxes,
  checkOfflineData: checkOfflineData // Explicitly map the function
};

// Attach the function directly to the exported object as backup
boxService.checkOfflineData = checkOfflineData;

export default boxService;
